import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

// Evento para volver a ver el tour (botón en Configuración).
export const EVENTO_VER_TOUR = 'evalpro:ver-tour';

// Cada paso señala un elemento con data-tour="..." (en Layout). Si no está a
// la vista (en el celular el menú está cerrado), la cajita sale centrada.
const PASOS = [
  {
    titulo: '¡Bienvenido a EVALPRO!',
    texto: 'Te muestro en un minuto dónde está cada cosa.',
  },
  {
    destino: 'nueva',
    titulo: 'Nuevo alumno',
    texto: 'Arrancás por la persona: hacés la entrevista guiada con ella adelante y al final EVALPRO te sugiere cómo avanzar. La ficha del alumno se arma sola.',
  },
  {
    destino: 'alumnos',
    titulo: 'Tus alumnos',
    texto: 'Todo está acá. Arriba, "Para hacer": lo que llegó por link, los aptos pendientes y los retests vencidos. Cada persona tiene su ficha con entrevistas, pruebas funcionales e informes.',
  },
  {
    destino: 'alumnos',
    titulo: 'El link para el alumno',
    texto: 'En Alumnos está "Mandar link de entrevista": se lo pasás por WhatsApp y la completa desde el celular antes de llegar.',
  },
  {
    destino: 'alumnos',
    titulo: 'Los informes',
    texto: 'En la ficha, en Informes, bajás el PDF de cada entrevista (una hoja, para el alumno y el médico) y el de seguimiento.',
  },
  {
    destino: 'ayuda',
    titulo: 'Si necesitás ayuda',
    texto: 'Acá tenés la Guía de uso y mi WhatsApp. Este tour lo podés volver a ver desde Configuración.',
  },
];

const MARGEN = 6;

function ubicacion(destino) {
  if (!destino) return null;
  const el = document.querySelector(`[data-tour="${destino}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  const visible = r.width > 0 && r.height > 0 && r.right > 0 && r.left < window.innerWidth;
  return visible ? r : null;
}

/**
 * Tour de bienvenida: cajitas que señalan cada parte de la app. Aparece una
 * sola vez por cuenta (queda guardado en el servidor); se puede saltear y
 * volver a ver desde Configuración.
 */
export default function TourBienvenida() {
  const { user, setSession } = useAuth();
  const [paso, setPaso] = useState(0);
  const [abierto, setAbierto] = useState(false);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (user && user.tour_visto === false) setAbierto(true);
  }, [user]);

  useEffect(() => {
    const ver = () => { setPaso(0); setAbierto(true); };
    window.addEventListener(EVENTO_VER_TOUR, ver);
    return () => window.removeEventListener(EVENTO_VER_TOUR, ver);
  }, []);

  const medir = useCallback(() => setRect(ubicacion(PASOS[paso]?.destino)), [paso]);
  useLayoutEffect(() => {
    if (!abierto) return undefined;
    medir();
    window.addEventListener('resize', medir);
    window.addEventListener('scroll', medir, true);
    return () => {
      window.removeEventListener('resize', medir);
      window.removeEventListener('scroll', medir, true);
    };
  }, [abierto, medir]);

  const cerrar = async () => {
    setAbierto(false);
    if (user && !user.tour_visto) {
      try {
        await api.marcarTourVisto();
        setSession({ token: localStorage.getItem('auth_token'), user: { ...user, tour_visto: true } });
      } catch (_e) {
        // Si falla, vuelve a aparecer la próxima vez: no es grave.
      }
    }
  };

  if (!abierto || !user) return null;

  const actual = PASOS[paso];
  const ultimo = paso === PASOS.length - 1;
  const enCelular = window.innerWidth < 640;

  // Posición de la cajita: al lado de lo señalado; si no hay, centrada (o
  // abajo, en el celular).
  let estiloCaja;
  if (rect && !enCelular) {
    const top = Math.min(Math.max(16, rect.top - 8), window.innerHeight - 260);
    estiloCaja = { top, left: rect.right + 18 };
  } else if (enCelular) {
    estiloCaja = { left: 16, right: 16, bottom: 16 };
  } else {
    estiloCaja = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Tour de bienvenida" data-testid="tour-bienvenida">
      {rect ? (
        <div
          className="absolute rounded-xl ring-2 ring-white transition-all duration-200 pointer-events-none"
          style={{
            top: rect.top - MARGEN,
            left: rect.left - MARGEN,
            width: rect.width + MARGEN * 2,
            height: rect.height + MARGEN * 2,
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.6)',
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-slate-900/60" />
      )}

      <div
        className="absolute w-auto sm:w-[22rem] bg-white rounded-2xl shadow-2xl p-5"
        style={estiloCaja}
      >
        <button
          type="button"
          onClick={cerrar}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
          aria-label="Cerrar el tour"
        >
          <X className="h-4 w-4" />
        </button>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          Paso {paso + 1} de {PASOS.length}
        </p>
        <p className="mt-1 text-lg font-bold text-[#1F3A5F]">{actual.titulo}</p>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          {actual.texto}
          {actual.destino && !rect ? ' Lo encontrás en el menú (☰), arriba a la derecha.' : ''}
        </p>
        <div className="mt-5 flex items-center justify-between gap-2">
          <button type="button" onClick={cerrar} className="text-sm text-slate-500 hover:text-slate-700" data-testid="tour-saltear">
            Saltear
          </button>
          <div className="flex gap-2">
            {paso > 0 && (
              <Button variant="outline" size="sm" onClick={() => setPaso(paso - 1)}>Atrás</Button>
            )}
            <Button
              size="sm"
              className="bg-[#1F3A5F] hover:bg-[#162c49] text-white"
              onClick={() => (ultimo ? cerrar() : setPaso(paso + 1))}
              data-testid="tour-siguiente"
            >
              {ultimo ? 'Listo' : paso === 0 ? 'Empezar' : 'Siguiente'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
